import { SerializedError } from '@reduxjs/toolkit';
import { useMemo } from 'react';
import { ExoApiError } from 'src/apis/baseQuery';
import { isExoApiError, isErrorWithMessage } from 'src/utils/errorHandlers';

/**
 * Custom hook to get error messages from RTK Query errors
 * Uses existing error type predicates for ExoApiError and SerializedError
 */
export const useApiErrorMessage = (error: ExoApiError | SerializedError): string | null => {
  return useMemo(() => {
    if (!error) return null;
    
    if (isExoApiError(error)) {
      // Prefer the server-provided detail (RFC 7807 ProblemDetails), then any
      // legacy `message` field, before falling back to canned status messages.
      if (error.data?.detail) {
        return error.data.detail;
      }
      if (error.data?.message) {
        return error.data.message;
      }

      switch (error.status) {
        case 400:
          return 'Bad request: Invalid parameters provided.';
        case 401:
          return 'Unauthorized: Please log in again.';
        case 403:
          return 'Forbidden: You do not have permission to access this data.';
        case 404:
          return 'Not found: The requested data could not be found.';
        case 500:
          return 'Server error: Something went wrong on our end.';
        case 503:
          return 'Service unavailable: The server is temporarily down.';
        default:
          return 'Something went wrong. Please try again later.';
      }
    }
    
    if (isErrorWithMessage(error)) {
      // Handle SerializedError
      return error.message || 'An unexpected error occurred while loading data.';
    }
    
    // Handle RTK Query specific error types
    if (typeof error === 'object' && error !== null && 'status' in error) {
      const rtkError = error as any;
      if (rtkError.status === 'FETCH_ERROR') {
        return 'Network error: Unable to fetch data. Please check your connection.';
      }
      if (rtkError.status === 'TIMEOUT_ERROR') {
        return 'Request timeout: The server took too long to respond.';
      }
      if (rtkError.status === 'PARSING_ERROR') {
        return 'Data parsing error: The server returned invalid data.';
      }
    }
    
    // Fallback error message
    return 'An unexpected error occurred while loading data.';
  }, [error]);
}; 